/**
 * USE PRODUCT CATALOG HOOK
 * 
 * Fetches products from the backend API
 * Replaces the static mock data with live data
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Product tier labels
export const TIER_LABELS = {
  builder: 'Builder',
  mid: 'Mid-Range',
  premium: 'Premium',
  luxury: 'Luxury',
};

// Map fixture keys to product categories/fixture_types
export const FIXTURE_TO_CATEGORY = {
  // Bathroom
  vanity: 'vanity',
  vanity_top: 'vanity_top',
  sink: 'sink',
  sink_faucet: 'faucet',
  toilet: 'toilet',
  bidet: 'bidet',
  tub: 'tub',
  tub_faucet: 'tub_faucet',
  shower_base: 'shower_base',
  shower_walls: 'shower_surround',
  shower_door: 'shower_enclosure',
  shower_fixture: 'shower',
  shower_niche: 'shower_niche',
  shower_bench: 'shower_bench',
  floor: 'flooring',
  walls: 'wall_tile',
  vanity_light: 'lighting',
  ceiling_light: 'lighting',
  exhaust_fan: 'exhaust_fan',
  mirror: 'bathroom_mirror',
  accessories: 'bathroom_accessories',
  
  // Kitchen
  base_cabinets: 'cabinets',
  wall_cabinets: 'cabinets',
  tall_cabinets: 'cabinets',
  island: 'cabinets',
  countertops: 'countertop',
  backsplash: 'backsplash',
  faucet: 'faucet',
  disposal: 'disposal',
  range: 'range',
  range_hood: 'range_hood',
  refrigerator: 'refrigerator',
  dishwasher: 'dishwasher',
  microwave: 'microwave',
  wall_oven: 'wall_oven',
  cooktop: 'cooktop',
  lighting: 'lighting',
  
  // Living/Bedroom
  baseboards: 'baseboards',
  crown_molding: 'crown_molding',
  ceiling: 'ceiling',
  ceiling_fan: 'ceiling_fan',
  windows: 'windows',
  interior_doors: 'interior_doors',
  closet: 'closet_system',
  fireplace: 'fireplace',
  built_ins: 'built_ins',
  
  // Exterior
  siding: 'siding',
  trim: 'exterior_trim',
  entry_door: 'entry_doors',
  patio_door: 'patio_doors',
  gutters: 'gutters',
  exterior_lighting: 'exterior_lighting',
  paint: 'paint',
  
  // Roof/Deck
  roofing: 'roofing',
  decking: 'deck_boards',
  railing: 'deck_railing',
};

// Get category/fixture_type for a fixture key
export const getCategoryForFixture = (fixtureKey) => {
  return FIXTURE_TO_CATEGORY[fixtureKey] || 'general';
};

// Fixture labels for display
export const FIXTURE_LABELS = {
  vanity: 'Vanity Cabinet',
  vanity_top: 'Vanity Top',
  sink: 'Sink',
  sink_faucet: 'Sink Faucet',
  toilet: 'Toilet',
  bidet: 'Bidet',
  tub: 'Bathtub',
  tub_faucet: 'Tub Faucet',
  shower_base: 'Shower Base',
  shower_walls: 'Shower Walls',
  shower_door: 'Shower Door',
  shower_fixture: 'Shower Fixture',
  shower_niche: 'Shower Niche',
  shower_bench: 'Shower Bench',
  floor: 'Floor',
  walls: 'Walls',
  vanity_light: 'Vanity Light',
  ceiling_light: 'Ceiling Light',
  exhaust_fan: 'Exhaust Fan',
  mirror: 'Mirror',
  accessories: 'Accessories',
  base_cabinets: 'Base Cabinets',
  wall_cabinets: 'Wall Cabinets',
  tall_cabinets: 'Tall Cabinets',
  island: 'Island',
  countertops: 'Countertops',
  backsplash: 'Backsplash',
  faucet: 'Faucet',
  disposal: 'Disposal',
  range: 'Range',
  range_hood: 'Range Hood',
  refrigerator: 'Refrigerator',
  dishwasher: 'Dishwasher',
  microwave: 'Microwave',
  wall_oven: 'Wall Oven',
  cooktop: 'Cooktop',
  lighting: 'Lighting',
  baseboards: 'Baseboards',
  crown_molding: 'Crown Molding',
  ceiling: 'Ceiling',
  ceiling_fan: 'Ceiling Fan',
  windows: 'Windows',
  interior_doors: 'Interior Doors',
  closet: 'Closet',
  fireplace: 'Fireplace',
  built_ins: 'Built-ins',
  siding: 'Siding',
  trim: 'Exterior Trim',
  entry_door: 'Entry Door',
  patio_door: 'Patio Door',
  gutters: 'Gutters',
  exterior_lighting: 'Exterior Lighting',
  paint: 'Paint',
  roofing: 'Roofing',
  decking: 'Decking',
  railing: 'Railing',
};

// Cabinet styles (static for now - can be moved to API later)
export const CABINET_STYLES = [
  {
    id: 'shaker',
    name: 'Shaker',
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=300&h=200&fit=crop',
    base_price_lf: 150,
    wall_price_lf: 120,
    tall_price_each: 450,
    tier: 'mid',
  },
  {
    id: 'flat_panel',
    name: 'Flat Panel / Slab',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=300&h=200&fit=crop',
    base_price_lf: 140,
    wall_price_lf: 110,
    tall_price_each: 420,
    tier: 'mid',
  },
  {
    id: 'raised_panel',
    name: 'Raised Panel',
    image: 'https://images.unsplash.com/photo-1556909190-eccf4a8bf97a?w=300&h=200&fit=crop',
    base_price_lf: 180,
    wall_price_lf: 150,
    tall_price_each: 550,
    tier: 'premium',
  },
  {
    id: 'beadboard',
    name: 'Beadboard',
    image: null,
    base_price_lf: 170,
    wall_price_lf: 140,
    tall_price_each: 520,
    tier: 'premium',
  },
  {
    id: 'glass_insert',
    name: 'Glass Insert',
    image: null,
    base_price_lf: 200,
    wall_price_lf: 180,
    tall_price_each: 600,
    tier: 'premium',
  },
];

// Cabinet finishes
export const CABINET_FINISHES = [
  // Painted
  { id: 'white', name: 'White', type: 'Painted', color: '#ffffff' },
  { id: 'gray', name: 'Gray', type: 'Painted', color: '#6b7280' },
  { id: 'navy', name: 'Navy', type: 'Painted', color: '#1e3a5f' },
  { id: 'black', name: 'Black', type: 'Painted', color: '#1f2937' },
  { id: 'sage', name: 'Sage Green', type: 'Painted', color: '#9caf88' },
  
  // Stained
  { id: 'natural', name: 'Natural Oak', type: 'Stained', color: '#d4a574' },
  { id: 'honey', name: 'Honey', type: 'Stained', color: '#c4883c' },
  { id: 'walnut', name: 'Walnut', type: 'Stained', color: '#5c4033' },
  { id: 'espresso', name: 'Espresso', type: 'Stained', color: '#3c2415' },
  { id: 'driftwood', name: 'Driftwood', type: 'Stained', color: '#8b8680' },
];

/**
 * Hook to fetch and manage products from the catalog API
 */
export const useProductCatalog = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);

  // Fetch all products
  const fetchProducts = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.fixture_type) params.append('fixture_type', filters.fixture_type);
      if (filters.tier) params.append('tier', filters.tier);
      if (filters.search) params.append('search', filters.search);
      params.append('limit', '100'); // Get more products
      
      const queryString = params.toString();
      const url = `${API_BASE}/api/catalog/products${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      
      const data = await response.json();
      setProducts(data.products || []);
      
      return data.products || [];
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/catalog/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  // Load products on mount
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  // Get products by fixture type
  const getProductsByFixture = useCallback((fixtureKey) => {
    const fixtureType = getCategoryForFixture(fixtureKey);
    return products.filter(p => 
      p.fixture_type === fixtureType || 
      p.category === fixtureType ||
      // Also match by broader category for bathroom/kitchen items
      (fixtureType && p.category?.includes(fixtureType.split('_')[0]))
    );
  }, [products]);

  // Get products by fixture and tier
  const getProductsByFixtureAndTier = useCallback((fixtureKey, tier) => {
    const fixtureProducts = getProductsByFixture(fixtureKey);
    if (tier === 'all') {
      return fixtureProducts;
    }
    return fixtureProducts.filter(p => p.tier === tier);
  }, [getProductsByFixture]);

  // Memoized helper to get products for a category
  const getProductsByCategory = useCallback((category) => {
    return products.filter(p => p.category === category || p.fixture_type === category);
  }, [products]);

  // Get products by category and tier
  const getProductsByCategoryAndTier = useCallback((category, tier) => {
    const categoryProducts = getProductsByCategory(category);
    if (tier === 'all') {
      return categoryProducts;
    }
    return categoryProducts.filter(p => p.tier === tier);
  }, [getProductsByCategory]);

  return {
    products,
    loading,
    error,
    categories,
    fetchProducts,
    getProductsByFixture,
    getProductsByFixtureAndTier,
    getProductsByCategory,
    getProductsByCategoryAndTier,
  };
};

export default useProductCatalog;
